import React, { useState, useEffect } from 'react';
import { assetsAPI } from '../services/api';
import AssetLiabilities from '../components/AssetLiabilities';
import ErrorBoundary from '../components/ErrorBoundary';
import { parseCalendarDay } from '../utils/localDate';

const Assets = ({ selectedBusiness }) => {
  const [assets, setAssets] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showLiabilities, setShowLiabilities] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadData();
  }, [selectedBusiness]);

  const loadData = async () => {
    try {
      const params = selectedBusiness ? { business_id: selectedBusiness } : {};
      
      const [assetsRes, statsRes] = await Promise.all([
        assetsAPI.getAll(params),
        assetsAPI.getStats(params),
      ]);
      
      setAssets(assetsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load assets:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    
    try {
      if (editingAsset) {
        await assetsAPI.update(editingAsset.id, data);
      } else {
        await assetsAPI.create(data);
      }
      setShowModal(false);
      setEditingAsset(null);
      loadData();
    } catch (error) {
      console.error('Failed to save asset:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this asset?')) {
      try {
        await assetsAPI.delete(id);
        loadData();
      } catch (error) {
        console.error('Failed to delete asset:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      }
    }
  };

  const handleViewLiabilities = (asset) => {
    setSelectedAsset(asset);
    setShowLiabilities(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getAssetIcon = (type) => {
    switch (type) {
      case 'vehicle': return '🚗';
      case 'property': return '🏠';
      case 'equipment': return '🔧';
      default: return '📦';
    }
  };

  return (
    <ErrorBoundary section="Assets">
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Assets</h2>
          <p className="text-gray-600 mt-1">Track your vehicles, property, and equipment</p>
        </div>
        <button
          onClick={() => { setShowModal(true); setEditingAsset(null); }}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          + Add Asset
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600">Total Assets</p>
            <p className="text-3xl font-bold text-gray-900">{stats.total_assets}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600">Purchase Value</p>
            <p className="text-3xl font-bold text-blue-600">{formatCurrency(stats.total_purchase_price)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600">Current Value</p>
            <p className="text-3xl font-bold text-green-600">{formatCurrency(stats.total_current_value)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600">Total Depreciation</p>
            <p className="text-3xl font-bold text-red-600">{formatCurrency(stats.total_depreciation)}</p>
          </div>
        </div>
      )}

      {/* Assets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {assets.map((asset) => {
          const depreciation = (asset.purchase_price || 0) - (asset.current_value || 0);
          const depreciationPercent = asset.purchase_price > 0 
            ? ((depreciation / asset.purchase_price) * 100).toFixed(1)
            : 0;

          return (
            <div key={asset.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-gray-700 to-gray-900 text-white p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-4xl">{getAssetIcon(asset.asset_type)}</span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    asset.is_active ? 'bg-green-500' : 'bg-gray-500'
                  }`}>
                    {asset.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <h3 className="text-xl font-bold">{asset.asset_name}</h3>
                <p className="text-sm opacity-90 capitalize">{asset.asset_type?.replace('_', ' ')}</p>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <p className="text-sm text-gray-600">Business</p>
                  <p className="font-semibold">{asset.business_name}</p>
                </div>
                
                {asset.purchase_date && (
                  <div>
                    <p className="text-sm text-gray-600">Purchase Date</p>
                    <p className="font-semibold">{parseCalendarDay(asset.purchase_date).toLocaleDateString()}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Purchase Price</p>
                    <p className="font-semibold text-blue-600">{formatCurrency(asset.purchase_price)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Current Value</p>
                    <p className="font-semibold text-green-600">{formatCurrency(asset.current_value)}</p>
                  </div>
                </div>
                
                <div className="bg-red-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Depreciation</p>
                  <p className="font-bold text-red-600">{formatCurrency(depreciation)}</p>
                  <p className="text-xs text-red-500 mt-1">{depreciationPercent}% decrease</p>
                </div>
                
                {asset.description && (
                  <div>
                    <p className="text-sm text-gray-600">Description</p>
                    <p className="text-sm">{asset.description}</p>
                  </div>
                )}
                
                <div className="flex space-x-2 pt-4 border-t">
                  <button
                    onClick={() => { setEditingAsset(asset); setShowModal(true); }}
                    className="flex-1 bg-blue-50 text-blue-600 px-4 py-2 rounded hover:bg-blue-100 transition"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleViewLiabilities(asset)}
                    className="flex-1 bg-purple-50 text-purple-600 px-4 py-2 rounded hover:bg-purple-100 transition font-medium"
                    title="View Liabilities"
                  >
                    💰 Liabilities
                  </button>
                  <button
                    onClick={() => handleDelete(asset.id)}
                    className="flex-1 bg-red-50 text-red-600 px-4 py-2 rounded hover:bg-red-100 transition"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">{editingAsset ? 'Edit' : 'Add'} Asset</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="hidden" name="business_id" value={selectedBusiness || editingAsset?.business_id || ''} />
              
              <div>
                <label className="block text-sm font-medium mb-1">Asset Name</label>
                <input
                  type="text"
                  name="asset_name"
                  defaultValue={editingAsset?.asset_name || ''}
                  required
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Asset Type</label>
                <select
                  name="asset_type"
                  defaultValue={editingAsset?.asset_type || 'vehicle'}
                  required
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="vehicle">Vehicle</option>
                  <option value="property">Property</option>
                  <option value="equipment">Equipment</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Purchase Date</label>
                <input
                  type="date"
                  name="purchase_date"
                  defaultValue={editingAsset?.purchase_date?.split('T')[0] || ''}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Purchase Price</label>
                <input
                  type="number"
                  name="purchase_price"
                  step="0.01"
                  defaultValue={editingAsset?.purchase_price || ''}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Current Value</label>
                <input
                  type="number"
                  name="current_value"
                  step="0.01"
                  defaultValue={editingAsset?.current_value || ''}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  name="description"
                  defaultValue={editingAsset?.description || ''}
                  rows="3"
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    defaultChecked={editingAsset?.is_active !== false}
                    className="mr-2"
                  />
                  <span className="text-sm">Active</span>
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingAsset(null); }}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingAsset ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Asset Liabilities Modal */}
      {showLiabilities && selectedAsset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold">
                Asset Liabilities - {selectedAsset.asset_name}
              </h2>
              <button
                onClick={() => {
                  setShowLiabilities(false);
                  setSelectedAsset(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <AssetLiabilities 
                assetId={selectedAsset.id}
                assetName={selectedAsset.asset_name}
                selectedBusiness={selectedBusiness}
              />
            </div>
          </div>
        </div>
      )}
    </div>
    </ErrorBoundary>
  );
};

export default Assets;
